<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "@/assets/api/openapi"

const operationStore = useOperationStore()
const { startLoading, stopLoading } = useLoading()
const { getKnowledgeExaminationDataAh } = useAppApi()

const knowledgeExamData =
  ref<components["schemas"]["KenntnispruefungAttempts"]>()

const schema = yup.object().shape({
  result: yup
    .string()
    .required("Pflichtfeld")
    .test(
      "is-not-open",
      "Der Arbeitsschritt kann nicht abgeschlossen werden, solange das Ergebnis nicht vorliegt",
      yupTestOnlyOnCreate((value) => value !== "notAvailable")
    ),

  dateDetermination: yup.string().when("result", {
    is: (v: string) => v && v !== "notAvailable",
    then: (s) => s.required("Pflichtfeld"),
    otherwise: (s) => s.strip(),
  }),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

const isDentist = computed(() => {
  return (knowledgeExamData.value?.attempts?.length ?? 0) === 3
})

const maxAttemptsReached = computed(() => {
  if (isDentist.value) {
    return (
      knowledgeExamData.value?.attempts?.some((attempt) => {
        return (attempt.numberOfAttempts ?? 0) >= 3 && !attempt.passed
      }) ?? false
    )
  } else {
    return (knowledgeExamData.value?.attempts?.[0].numberOfAttempts ?? 0) >= 3
  }
})

const resultOptions = [
  {
    value: "notAvailable",
    label: "Liegt noch nicht vor",
  },
  {
    value: "equivalent",
    label: "Gleichwertig nach Kenntnisprüfung",
  },
  {
    value: "notEquivalent",
    label: "Nicht gleichwertig nach Kenntnisprüfung",
  },
  {
    value: "canceled",
    label: "Kenntnisprüfung abgebrochen ohne Ergebnis",
  },
]

const knowledgeTestDataFields = computed<DataField[]>(() => {
  const result: DataField[] = [
    {
      label: "Kenntnisprüfung",
      value: statusText.value,
    },
  ]

  if (!isDentist.value) {
    result.push({
      label: "Anzahl gewerteter Versuche",
      value:
        knowledgeExamData.value?.attempts &&
        knowledgeExamData.value.attempts.length > 0
          ? knowledgeExamData.value.attempts[0].numberOfAttempts
          : "-",
    })
  }

  if (isDentist.value) {
    knowledgeExamData.value?.attempts?.forEach((attempt) => {
      let label = ""
      if (attempt.examType === "written") {
        label = "Anzahl gewerteter Versuche (schriftlich)"
      } else if (attempt.examType === "oral") {
        label = "Anzahl gewerteter Versuche (mündlich)"
      } else if (attempt.examType === "practical") {
        label = "Anzahl gewerteter Versuche (praktisch)"
      }

      if (label !== "") {
        result.push({
          label,
          value: attempt.numberOfAttempts ?? "-",
        })
      }
    })
  }

  return result
})

const statusTextMap: Record<string, string> = {
  approved: "Bestanden am <DATE>",
  rejected: "Nicht bestanden am <DATE>",
  rejectedPatientRisk: "Nicht bestanden (Patientengefährdung) am <DATE>",
  absent: "Nicht angetreten am <DATE>",
  canceled: "Abbruch (mangelnde Sprachkenntnisse) am <DATE>",
  registered: "Angemeldet am <DATE>",
  unregistered: "Abgemeldet am <DATE>",
}

const statusText = computed(() => {
  const value = knowledgeExamData.value?.lastKenntnispruefungResult
  if (!value) return "-"
  return statusTextMap[value].replace(
    "<DATE>",
    knowledgeExamData.value?.lastKenntnispruefungDate ?? "?"
  )
})

const fetchData = async () => {
  if (operationStore.currentWorkStepId) {
    try {
      startLoading()

      const response = await getKnowledgeExaminationDataAh(
        operationStore.currentWorkStepId
      )

      if (response.status === 200 && response.data) {
        knowledgeExamData.value = response.data
      }
    } catch {
      showError({
        statusCode: 500,
      })
    } finally {
      stopLoading()
    }
  }
}
</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Organisation Kenntnisprüfung"
        @call-update="fetchData()"
      />
    </template>

    <IffContentSection headline="Stand der Kenntnisprüfung">
      <IffDataFields :data="knowledgeTestDataFields" />
    </IffContentSection>

    <IffContentSection
      headline="Abschließendes Ergebnis nach Kenntnisprüfung"
      :is-last-child="true"
    >
      <IffMessage
        v-if="maxAttemptsReached"
        variant="warning"
        class="max-w-[600px] mb-3"
      >
        <IffCopyText size="small">
          Die maximale Anzahl an Versuchen wurde erreicht, bitte erfassen Sie
          das abschließende Ergebnis.
        </IffCopyText>
      </IffMessage>

      <IffRadioGroup
        legend="Ergebnis"
        name="result"
        v-model="state.result"
        :options="resultOptions"
        required
        aria-controls="determination-date-region"
      />
      <div id="determination-date-region" aria-live="polite">
        <IffTransitionHeight>
          <UFormGroup
            v-if="state.result && state.result !== 'notAvailable'"
            name="dateDetermination"
            label="Datum der Feststellung"
            required
            class="max-w-[200px]"
          >
            <template #hint>
              <UButton
                @click="
                  state.dateDetermination = new Date()
                    .toISOString()
                    .slice(0, 10)
                "
                icon="i-ic-outline-access-time"
                size="sm"
                color="gray"
                variant="ghost"
                :trailing="false"
                aria-label="Aktuelles Datum wählen"
                title="Aktuelles Datum wählen"
              />
            </template>

            <UInput
              v-model="state.dateDetermination"
              type="date"
              aria-required="true"
              class="whitespace-normal max-w-[150px]"
            />
          </UFormGroup>
        </IffTransitionHeight>
      </div>
    </IffContentSection>
  </IffOperationWorkStepFormV2>
</template>
